import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useInventory } from '../../contexts/InventoryContext'
import { useAuth } from '../../contexts/AuthContext'
import { supabase } from '../../lib/supabase'
import { useB2BBuyers } from '../../hooks/useB2BBuyers'
import {
  useB2BAdminInvoices,
  type AdminInvoiceRow,
  type InvoiceEditContext,
} from '../../hooks/useB2BAdminInvoices'
import { Button } from '../ui/Button'
import { getAdminId } from '../../utils/getAdminId'
import {
  FileText, Search, Plus, Minus, Trash2, X, Send, Pencil, Loader2, AlertTriangle, Printer,
} from 'lucide-react'
import type { B2BBuyer } from '../../types/b2b'

interface EditorLine {
  product_id: string
  name: string
  sku?: string
  color_name?: string
  size_name?: string
  base: number
  quantity: number
}

interface ColorSize {
  id: string
  name: string
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

export function B2BAdminInvoiceManager() {
  const { user } = useAuth()
  const { allItems, fetchAllItems } = useInventory()
  const { buyers, loading: buyersLoading } = useB2BBuyers()
  const {
    invoices, loading, error, loadInvoicesForBuyer, isInvoiceEditable, loadEditContext, saveInvoice, buildInvoiceHtml, resendInvoice,
  } = useB2BAdminInvoices()

  const [selectedBuyerId, setSelectedBuyerId] = useState('')
  const [colors, setColors] = useState<ColorSize[]>([])
  const [sizes, setSizes] = useState<ColorSize[]>([])

  const [editingInvoice, setEditingInvoice] = useState<AdminInvoiceRow | null>(null)
  const [editContext, setEditContext] = useState<InvoiceEditContext | null>(null)
  const [divisor, setDivisor] = useState(1)
  const [lines, setLines] = useState<EditorLine[]>([])
  const [productSearch, setProductSearch] = useState('')
  const [saving, setSaving] = useState(false)
  const [resendingId, setResendingId] = useState<string | null>(null)
  const [printingId, setPrintingId] = useState<string | null>(null)
  const [feedback, setFeedback] = useState<{ kind: 'success' | 'error'; text: string } | null>(null)

  const selectedBuyer = useMemo<B2BBuyer | null>(
    () => buyers.find(b => b.id === selectedBuyerId) ?? null,
    [buyers, selectedBuyerId],
  )

  const currency = selectedBuyer?.preferred_currency || 'EUR'
  const vatPct = selectedBuyer?.vat_percentage ?? 21
  const formatPrice = useCallback(
    (amount: number) => new Intl.NumberFormat('en-EU', { style: 'currency', currency }).format(amount || 0),
    [currency],
  )

  useEffect(() => {
    if (user) fetchAllItems()
  }, [user, fetchAllItems])

  useEffect(() => {
    const loadColorsSizes = async () => {
      if (!user) return
      const adminId = getAdminId(user)
      if (!adminId) return
      const [colorsRes, sizesRes] = await Promise.all([
        supabase.from('product_colors').select('id, name').eq('admin_id', adminId).order('name'),
        supabase.from('product_sizes').select('id, name').eq('admin_id', adminId).order('sort_order'),
      ])
      if (colorsRes.data) setColors(colorsRes.data)
      if (sizesRes.data) setSizes(sizesRes.data)
    }
    void loadColorsSizes()
  }, [user])

  useEffect(() => {
    if (selectedBuyerId) void loadInvoicesForBuyer(selectedBuyerId)
  }, [selectedBuyerId, loadInvoicesForBuyer])

  const getColorName = useCallback((colorId?: string) => colors.find(c => c.id === colorId)?.name, [colors])
  const getSizeName = useCallback((sizeId?: string) => sizes.find(s => s.id === sizeId)?.name, [sizes])

  const availableFor = useCallback(
    (productId: string): number => {
      if (!editContext) return 0
      const wh = editContext.warehouseQty[productId] ?? 0
      const others = editContext.reservedByOthers[productId] ?? 0
      return Math.max(0, wh - others)
    },
    [editContext],
  )

  const openEditor = useCallback(
    async (invoice: AdminInvoiceRow) => {
      setFeedback(null)
      setEditingInvoice(invoice)
      setDivisor(invoice.account_discount ?? selectedBuyer?.account_discount ?? 1)
      // Initialise lines from invoice items; base = captured base, else catalog price.
      const initial: EditorLine[] = (invoice.invoice_items || []).map(it => {
        const catalog = allItems.find(p => p.id === it.product_id)
        return {
          product_id: it.product_id || '',
          name: it.product_name || catalog?.name || it.description || 'Item',
          sku: it.sku || catalog?.sku || undefined,
          color_name: it.color_name || getColorName(catalog?.color_id) || undefined,
          size_name: it.size_name || getSizeName(catalog?.size_id) || undefined,
          base: it.base_unit_price ?? catalog?.sell_price ?? it.unit_price ?? 0,
          quantity: it.quantity,
        }
      })
      setLines(initial.filter(l => l.product_id))
      try {
        const ctx = await loadEditContext(invoice)
        setEditContext(ctx)
      } catch (err) {
        setEditingInvoice(null)
        setLines([])
        setFeedback({
          kind: 'error',
          text: err instanceof Error ? `Could not load stock: ${err.message}` : 'Could not load warehouse stock for this invoice.',
        })
      }
    },
    [allItems, getColorName, getSizeName, loadEditContext, selectedBuyer],
  )

  const closeEditor = useCallback(() => {
    setEditingInvoice(null)
    setEditContext(null)
    setLines([])
    setProductSearch('')
  }, [])

  const updateQty = useCallback(
    (productId: string, qty: number) => {
      setFeedback(null)
      setLines(prev =>
        prev.flatMap(l => {
          if (l.product_id !== productId) return [l]
          if (qty <= 0) return []
          const max = availableFor(productId)
          return [{ ...l, quantity: Math.min(qty, max) }]
        }),
      )
    },
    [availableFor],
  )

  const removeLine = useCallback((productId: string) => {
    setFeedback(null)
    setLines(prev => prev.filter(l => l.product_id !== productId))
  }, [])

  const addProduct = useCallback(
    (productId: string) => {
      const p = allItems.find(i => i.id === productId)
      if (!p) return
      if (lines.some(l => l.product_id === productId)) return
      setFeedback(null)
      const max = availableFor(productId)
      if (max <= 0) return
      setLines(prev => [
        ...prev,
        {
          product_id: p.id,
          name: p.name,
          sku: p.sku || undefined,
          color_name: getColorName(p.color_id) || undefined,
          size_name: getSizeName(p.size_id) || undefined,
          base: p.sell_price ?? 0,
          quantity: 1,
        },
      ])
      setProductSearch('')
    },
    [allItems, lines, availableFor, getColorName, getSizeName],
  )

  const lineUnit = useCallback((base: number) => (divisor > 0 ? round2(base / divisor) : 0), [divisor])

  const totals = useMemo(() => {
    const subtotal = round2(lines.reduce((sum, l) => sum + lineUnit(l.base) * l.quantity, 0))
    const tax = round2(subtotal * (vatPct / 100))
    return { subtotal, tax, total: round2(subtotal + tax) }
  }, [lines, lineUnit, vatPct])

  const productPickerResults = useMemo(() => {
    const q = productSearch.trim().toLowerCase()
    if (!q) return []
    const chosen = new Set(lines.map(l => l.product_id))
    return allItems
      .filter(p => !chosen.has(p.id) && (p.sell_price ?? 0) > 0)
      .filter(p =>
        p.name?.toLowerCase().includes(q) ||
        p.sku?.toLowerCase().includes(q) ||
        p.reference_code?.toLowerCase().includes(q),
      )
      .slice(0, 8)
  }, [productSearch, allItems, lines])

  const canSave = lines.length > 0 && divisor > 0 && !!editContext?.warehouseLocationId

  const handleSave = useCallback(async () => {
    if (!editingInvoice || !editContext?.warehouseLocationId) return
    setSaving(true)
    setFeedback(null)
    const result = await saveInvoice({
      invoiceId: editingInvoice.id,
      divisor,
      vatPct,
      sourceLocationId: editContext.warehouseLocationId,
      items: lines.map(l => ({
        product_id: l.product_id,
        quantity: l.quantity,
        product_name: l.name,
        sku: l.sku,
        color_name: l.color_name,
        size_name: l.size_name,
      })),
    })
    setSaving(false)
    if (!result.success) {
      setFeedback({ kind: 'error', text: result.error || 'Failed to save invoice' })
      return
    }
    setFeedback({ kind: 'success', text: 'Invoice and reservation updated. The customer now sees the new invoice.' })
    closeEditor()
    if (selectedBuyerId) void loadInvoicesForBuyer(selectedBuyerId)
  }, [editingInvoice, editContext, divisor, vatPct, lines, saveInvoice, closeEditor, selectedBuyerId, loadInvoicesForBuyer])

  const handleResend = useCallback(
    async (invoice: AdminInvoiceRow) => {
      if (!selectedBuyer) return
      setResendingId(invoice.id)
      setFeedback(null)
      const result = await resendInvoice(invoice, selectedBuyer)
      setResendingId(null)
      setFeedback(
        result.success
          ? { kind: 'success', text: `Updated invoice #${invoice.invoice_number} emailed to ${selectedBuyer.email}.` }
          : { kind: 'error', text: result.error || 'Failed to resend invoice' },
      )
    },
    [selectedBuyer, resendInvoice],
  )

  const handlePrint = useCallback(
    async (invoice: AdminInvoiceRow) => {
      if (!selectedBuyer) return
      // Open synchronously in the click handler so popup blockers do not kill it after await.
      const printWindow = window.open('', '_blank')
      if (!printWindow) {
        setFeedback({
          kind: 'error',
          text: 'Popups are blocked. Allow popups for this site and try Print copy again.',
        })
        return
      }

      setPrintingId(invoice.id)
      setFeedback(null)
      try {
        const html = await buildInvoiceHtml(invoice, selectedBuyer)
        const autoPrintScript = `
<script>
  window.onload = () => {
    setTimeout(() => {
      window.print()
      window.onafterprint = () => window.close()
    }, 250)
  }
</script>`
        const printableHtml = html.includes('</body>')
          ? html.replace('</body>', `${autoPrintScript}</body>`)
          : `${html}${autoPrintScript}`
        printWindow.document.write(printableHtml)
        printWindow.document.close()
      } catch (err) {
        printWindow.close()
        setFeedback({
          kind: 'error',
          text: err instanceof Error ? err.message : 'Failed to prepare invoice for printing.',
        })
      } finally {
        setPrintingId(null)
      }
    },
    [selectedBuyer, buildInvoiceHtml],
  )

  return (
    <div className="card p-5 space-y-5">
      <div className="flex items-center gap-2">
        <FileText className="h-5 w-5 text-blue-600" />
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Manage customer invoices</h2>
      </div>
      <p className="text-sm text-gray-600 dark:text-gray-400 -mt-3">
        Select a B2B customer to view and edit their invoices. Edits apply to the linked reservation and are visible to the customer immediately.
      </p>

      {/* Customer selector */}
      <div className="max-w-md">
        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">Customer</label>
        <select
          value={selectedBuyerId}
          onChange={e => { setSelectedBuyerId(e.target.value); setFeedback(null) }}
          className="input w-full h-11"
          disabled={buyersLoading}
        >
          <option value="">{buyersLoading ? 'Loading customers...' : 'Select a customer...'}</option>
          {buyers.map(b => (
            <option key={b.id} value={b.id}>{b.trade_name} ({b.buyer_code})</option>
          ))}
        </select>
      </div>

      {feedback && (
        <div
          className={`rounded-lg px-4 py-3 text-sm ${
            feedback.kind === 'success'
              ? 'bg-green-50 text-green-700 dark:bg-green-900/20 dark:text-green-400'
              : 'bg-red-50 text-red-700 dark:bg-red-900/20 dark:text-red-400'
          }`}
        >
          {feedback.text}
        </div>
      )}

      {/* Invoice list */}
      {selectedBuyerId && (
        <div className="space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-10 text-gray-500">
              <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading invoices...
            </div>
          ) : error ? (
            <div className="text-sm text-red-600">{error}</div>
          ) : invoices.length === 0 ? (
            <div className="text-center py-10 text-sm text-gray-500 dark:text-gray-400">
              No invoices found for this customer.
            </div>
          ) : (
            invoices.map(inv => {
              const editable = isInvoiceEditable(inv)
              const status = inv.reservation?.status
              return (
                <div
                  key={inv.id}
                  className="flex flex-wrap items-center justify-between gap-3 border border-gray-200 dark:border-gray-700 rounded-lg p-3"
                >
                  <div className="min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="font-semibold text-gray-900 dark:text-gray-100">#{inv.invoice_number}</span>
                      {status && (
                        <span className={`text-xs font-medium px-2 py-0.5 rounded-full ${
                          editable
                            ? 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300'
                            : 'bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400'
                        }`}>
                          {status}
                        </span>
                      )}
                      {!inv.reservation_id && (
                        <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-gray-100 text-gray-600 dark:bg-gray-700 dark:text-gray-400">
                          no reservation
                        </span>
                      )}
                    </div>
                    <div className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {new Date(inv.invoice_date).toLocaleDateString()} · {formatPrice(inv.total_amount)}
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="secondary"
                      onClick={() => handleResend(inv)}
                      disabled={resendingId === inv.id}
                      className="h-9 text-sm"
                    >
                      {resendingId === inv.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                      <span className="ml-1.5">Resend</span>
                    </Button>
                    {editable ? (
                      <Button onClick={() => openEditor(inv)} className="h-9 text-sm">
                        <Pencil className="h-4 w-4" /><span className="ml-1.5">Edit</span>
                      </Button>
                    ) : (
                      <Button
                        variant="secondary"
                        onClick={() => handlePrint(inv)}
                        disabled={printingId === inv.id}
                        className="h-9 text-sm"
                      >
                        {printingId === inv.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Printer className="h-4 w-4" />}
                        <span className="ml-1.5">Print copy</span>
                      </Button>
                    )}
                  </div>
                </div>
              )
            })
          )}
        </div>
      )}

      {/* Editor modal */}
      {editingInvoice && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={closeEditor}>
          <div
            className="bg-white dark:bg-gray-800 rounded-xl shadow-xl w-full max-w-3xl max-h-[90vh] overflow-y-auto"
            onClick={e => e.stopPropagation()}
          >
            <div className="sticky top-0 bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-5 py-4 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                Edit invoice #{editingInvoice.invoice_number}
              </h3>
              <button onClick={closeEditor} className="p-1.5 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700">
                <X className="h-5 w-5 text-gray-500" />
              </button>
            </div>

            <div className="p-5 space-y-5">
              {!editContext ? (
                <div className="flex items-center justify-center py-8 text-gray-500">
                  <Loader2 className="h-5 w-5 animate-spin mr-2" /> Loading stock...
                </div>
              ) : (
                <>
                  {!editContext.warehouseLocationId && (
                    <div className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-900/20 px-4 py-3 text-sm text-amber-700 dark:text-amber-400">
                      <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      No warehouse location is configured, so this invoice cannot be saved. Set up a Warehouse location first.
                    </div>
                  )}

                  {/* Divisor */}
                  <div className="max-w-xs">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1.5">
                      Account Discount (Divisor)
                    </label>
                    <input
                      type="number"
                      min="0.01"
                      step="0.01"
                      value={divisor}
                      onChange={e => { setDivisor(parseFloat(e.target.value) || 0); setFeedback(null) }}
                      className="input w-full h-10"
                    />
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-1">
                      Final price = Sell price ÷ this number. Applies to this invoice only.
                    </p>
                  </div>

                  {/* Lines */}
                  <div className="space-y-2">
                    {lines.length === 0 && (
                      <p className="text-sm text-gray-500 dark:text-gray-400">No items. Add at least one product below.</p>
                    )}
                    {lines.map(l => {
                      const unit = lineUnit(l.base)
                      const max = availableFor(l.product_id)
                      return (
                        <div key={l.product_id} className="flex items-center gap-3 border border-gray-200 dark:border-gray-700 rounded-lg p-2.5">
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{l.name}</p>
                            <p className="text-xs text-gray-500 dark:text-gray-400">
                              {[l.sku, l.color_name, l.size_name].filter(Boolean).join(' · ')}
                              {l.sku || l.color_name || l.size_name ? ' · ' : ''}
                              {formatPrice(unit)} each · max {max}
                            </p>
                          </div>
                          <div className="flex items-center gap-1.5">
                            <button
                              onClick={() => updateQty(l.product_id, l.quantity - 1)}
                              className="w-7 h-7 rounded-lg bg-gray-100 dark:bg-gray-700 flex items-center justify-center hover:bg-gray-200 dark:hover:bg-gray-600"
                            >
                              <Minus className="h-3.5 w-3.5" />
                            </button>
                            <span className="w-8 text-center text-sm font-bold">{l.quantity}</span>
                            <button
                              onClick={() => updateQty(l.product_id, l.quantity + 1)}
                              disabled={l.quantity >= max}
                              className="w-7 h-7 rounded-lg bg-blue-600 text-white flex items-center justify-center hover:bg-blue-700 disabled:opacity-40 disabled:cursor-not-allowed"
                            >
                              <Plus className="h-3.5 w-3.5" />
                            </button>
                          </div>
                          <div className="w-20 text-right text-sm font-semibold">{formatPrice(unit * l.quantity)}</div>
                          <button
                            onClick={() => removeLine(l.product_id)}
                            className="p-1.5 text-red-400 hover:text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg"
                          >
                            <Trash2 className="h-4 w-4" />
                          </button>
                        </div>
                      )
                    })}
                  </div>

                  {/* Add product */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 pointer-events-none" />
                    <input
                      type="text"
                      value={productSearch}
                      onChange={e => setProductSearch(e.target.value)}
                      placeholder="Add a product by name, SKU or reference..."
                      className="input pl-10 w-full h-10"
                    />
                    {productPickerResults.length > 0 && (
                      <div className="absolute z-10 mt-1 w-full bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg max-h-64 overflow-y-auto">
                        {productPickerResults.map(p => {
                          const max = availableFor(p.id)
                          return (
                            <button
                              key={p.id}
                              onClick={() => addProduct(p.id)}
                              disabled={max <= 0}
                              className="w-full text-left px-3 py-2 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-between gap-2"
                            >
                              <span className="min-w-0">
                                <span className="block text-sm font-medium text-gray-900 dark:text-gray-100 truncate">{p.name}</span>
                                <span className="block text-xs text-gray-500 dark:text-gray-400">
                                  {[p.sku, getColorName(p.color_id), getSizeName(p.size_id)].filter(Boolean).join(' · ')}
                                </span>
                              </span>
                              <span className="text-xs text-gray-400 flex-shrink-0">{max} avail</span>
                            </button>
                          )
                        })}
                      </div>
                    )}
                  </div>

                  {/* Totals */}
                  <div className="border-t border-gray-200 dark:border-gray-700 pt-3 space-y-1.5 max-w-xs ml-auto">
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                      <span>Subtotal excl. BTW</span><span className="font-medium">{formatPrice(totals.subtotal)}</span>
                    </div>
                    <div className="flex justify-between text-sm text-gray-600 dark:text-gray-400">
                      <span>{vatPct}% BTW</span><span className="font-medium">{formatPrice(totals.tax)}</span>
                    </div>
                    <div className="flex justify-between text-base font-bold text-gray-900 dark:text-gray-100 pt-1.5 border-t border-gray-200 dark:border-gray-700">
                      <span>Total incl. BTW</span><span className="text-blue-600 dark:text-blue-400">{formatPrice(totals.total)}</span>
                    </div>
                  </div>

                  {feedback?.kind === 'error' && (
                    <div className="flex items-start gap-2 rounded-lg bg-red-50 dark:bg-red-900/20 px-4 py-3 text-sm text-red-700 dark:text-red-400">
                      <AlertTriangle className="h-4 w-4 mt-0.5 flex-shrink-0" />
                      <span>{feedback.text}</span>
                    </div>
                  )}

                  <div className="flex justify-end gap-2 pt-2">
                    <Button variant="secondary" onClick={closeEditor} disabled={saving}>Cancel</Button>
                    <Button onClick={handleSave} disabled={!canSave || saving}>
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
                      <span className={saving ? 'ml-1.5' : ''}>{saving ? 'Saving...' : 'Save changes'}</span>
                    </Button>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}
